import { HttpClient, HttpParams, HttpResponse } from "@angular/common/http";
import { Injectable } from "@angular/core";
import * as moment from "moment";
import { Observable } from "rxjs";
import { forkJoin } from "rxjs/observable/forkJoin";
import { map, timeout } from "rxjs/operators";
import { ConfigurationService } from "../configuration/configuration.service";

const CARD_DATE_FORMAT = "MM/DD/YYYY";
const SERVER_DATE_FORMAT = "YYYY-MM-DD";

// qapiplus-service's ALB idle timeout is the AWS default 60s (no override is
// set in its ecs config). 55s beats that infra cutoff so the user gets the
// BDD F9.S2 "Export took too long" message instead of an opaque 504.
// PROPOSAL, not a ruled number - flag for review.
export const EXPORT_TIMEOUT_MS = 55000;

export interface QapiPlusIncidentType {
  serverType: string;
  filenameSlug: string;
}

// Server enum names and the filename slug BDD F3.S1 pins for each.
export const QAPI_PLUS_INCIDENT_TYPES: QapiPlusIncidentType[] = [
  { serverType: "FALL", filenameSlug: "fall" },
  { serverType: "MEDICATION_ERROR", filenameSlug: "medication_error" },
  { serverType: "PATIENT_INFECTION", filenameSlug: "patient_infection" },
  {
    serverType: "ADVERSE_DRUG_REACTION",
    filenameSlug: "adverse_drug_reaction",
  },
  { serverType: "UNUSUAL_OCCURRENCE", filenameSlug: "unusual_occurrence" },
  { serverType: "SENTINEL_EVENT", filenameSlug: "sentinel_event" },
];

export interface QapiPlusExportResult {
  filenameSlug: string;
  response: HttpResponse<Blob>;
}

@Injectable()
export class QapiPlusExportDataService {
  constructor(private http: HttpClient, private config: ConfigurationService) {}

  /**
   * Issues the six per-incident-type export GETs in parallel.
   *
   * forkJoin is all-or-nothing by construction: if any one of the six
   * requests errors or times out, forkJoin errors and none of the six
   * per-request results ever reach a subscriber - so a caller subscribing to
   * this Observable can never save a partial set of files. This is the
   * AC-016-2 zero-files guarantee (BDD F9.S1): it comes from forkJoin's own
   * contract, not from application logic that could get it wrong.
   */
  downloadAll(
    from: string,
    through: string
  ): Observable<QapiPlusExportResult[]> {
    const baseUrl = this.config.resolveQapiplusUrl();
    const isoFrom = moment(from, CARD_DATE_FORMAT, true).format(
      SERVER_DATE_FORMAT
    );
    const isoThrough = moment(through, CARD_DATE_FORMAT, true).format(
      SERVER_DATE_FORMAT
    );

    const requests = QAPI_PLUS_INCIDENT_TYPES.map(
      ({ serverType, filenameSlug }) => {
        const params = new HttpParams()
          .set("incidentType", serverType)
          .set("from", isoFrom)
          .set("through", isoThrough);
        return this.http
          .get(`${baseUrl}/qapiplus/export`, {
            params,
            responseType: "blob",
            observe: "response",
          })
          .pipe(
            timeout(EXPORT_TIMEOUT_MS),
            map((response: HttpResponse<Blob>) => ({ filenameSlug, response }))
          );
      }
    );

    return forkJoin(requests);
  }
}



// WEBPACK FOOTER //
// ./src/providers/qapi-plus-export/qapi-plus-export.data.service.ts